/**
 * Sanitizes a symbol ensuring that it will always have the given length
 *
 * @param symbol - the symbol to be sanitized
 * @param length - the max length of the symbol after sanitizing, defaults to 1
 *
 * @returns a sanitized symbol
 * @internal
 */
export const _sanitizeSymbol = (symbol: string, length: number = 1): string => {
  let symbolAcc: string[] = [];

  if (typeof Intl.Segmenter === 'function') {
    // Segmenter is used to split the symbol into graphemes
    symbolAcc = segmentWithIntlSegmenter(symbol, length);
  } else {
    // If the Segmenter API is not available, we fallback to a simple loop
    symbolAcc = segmentWithFallback(symbol, length);
  }

  return symbolAcc.join('');
};

function segmentWithIntlSegmenter(symbol: string, length: number): string[] {
  const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
  const symbolAcc: string[] = [];

  for (const segment of segmenter.segment(symbol)) {
    if (symbolAcc.length === length) {
      break;
    }
    symbolAcc.push(segment.segment);
  }

  return symbolAcc;
}

function segmentWithFallback(symbol: string, length: number): string[] {
  const symbolTokens = [...symbol];
  const symbolAcc: string[] = [];

  let i = 0;
  let j = 0;
  while (i < symbolTokens.length && j < length) {
    symbolAcc.push(symbolTokens[i]);

    const isEndOfSymbol = i === symbolTokens.length - 1;

    if (isEndOfSymbol) {
      break;
    }
    const nextToken = symbolTokens[i + 1];
    if (isUnicodeSymbolVariation(nextToken)) {
      symbolAcc.push(nextToken);
      i++;
      j++;
    } else if (isEmojiSeparator(nextToken)) {
      // if next token is a composed emoji continuation symbol
      symbolAcc.push(nextToken);
      i++;
    } else {
      j++;
    }

    i++;
  }

  return symbolAcc;
}

/**
 * Unicode's symbols have an empty-string-like symbol as suffix
 * which has 65038 char code.
 *
 * @param char - the char to test
 *
 * @returns true if the char is unicode symbol separator
 */
const isUnicodeSymbolVariation = (char: string) => char.charCodeAt(0) === 65038;

/**
 * Composed emojis are divided into smaller emojis separated by
 * \\u200d or '' or '️' (zero-width joiner) unicode symbols
 *
 * @param char - the char to test
 *
 * @returns true if the char is a zero-width joiner.
 */
const isEmojiSeparator = (char: string) =>
  char === '\u200d' || char === '' || char === '️';
